import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { YourGiftCardPreview } from '../components/YourGiftCardPreview';
import { GiftCardStepper } from '../components/GiftCardStepper';
import {
  giftCardApiErrorMessage,
  purchaseGiftCard,
} from '../../lib/gift-card-api';
import { useCustomerAuth } from '../../lib/use-customer-auth';
import {
  getGiftCardDraft,
  patchGiftCardDraft,
} from '../../lib/gift-card-draft';
import { sanitizeName } from '../../lib/validators';
import { toast } from '../../lib/toast';

function formatDate(iso?: string) {
  if (!iso) return 'Send immediately';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function GiftCardSummaryPage() {
  const navigate = useNavigate();
  // Read every field from the persistent draft so the summary always
  // reflects what the customer actually entered, even if they came
  // back to this page via Back / refresh.
  const draft = getGiftCardDraft();
  const incoming = draft ?? {};
  const amount = incoming.amount ?? 50;
  const recipientName =
    [incoming.firstName, incoming.lastName].filter(Boolean).join(' ') || '-';
  const isSelfDelivery = incoming.method === 'SELF';

  const { user: customer } = useCustomerAuth();

  const [buyerFirst, setBuyerFirst] = useState(incoming.buyerFirstName ?? '');
  const [buyerLast, setBuyerLast] = useState(incoming.buyerLastName ?? '');
  const [buyerEmail, setBuyerEmail] = useState(incoming.buyerEmail ?? '');
  const [buyerFirstError, setBuyerFirstError] = useState<string | null>(null);
  const [buyerLastError, setBuyerLastError] = useState<string | null>(null);
  const [buyerEmailError, setBuyerEmailError] = useState<string | null>(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);

  // Pre-fill the buyer fields from the signed-in customer's profile so
  // we don't ask logged-in users for details we already have. Only
  // fills empties so a user mid-typing isn't clobbered if the auth
  // store updates underneath us.
  useEffect(() => {
    if (!customer) return;
    setBuyerFirst((prev) => prev || customer.first_name?.trim() || '');
    setBuyerLast((prev) => prev || customer.last_name?.trim() || '');
    setBuyerEmail((prev) => prev || customer.email?.trim() || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  // Quick client-side gates so the form doesn't lob obvious garbage at
  // the backend. Real validation happens server-side via Joi.
  const isReady =
    buyerFirst.trim().length > 0 &&
    buyerLast.trim().length > 0 &&
    /^\S+@\S+\.\S+$/.test(buyerEmail.trim()) &&
    !!incoming.firstName &&
    !!incoming.lastName &&
    !!incoming.email &&
    amount > 0;

  const getValidationError = (): string | null => {
    // Check all fields and set all errors at once (not early return)
    const errors: string[] = [];

    setBuyerFirstError(null);
    setBuyerLastError(null);
    setBuyerEmailError(null);

    if (!buyerFirst.trim()) {
      setBuyerFirstError('Please enter your first name.');
      errors.push('first name');
    }
    if (!buyerLast.trim()) {
      setBuyerLastError('Please enter your last name.');
      errors.push('last name');
    }

    const email = buyerEmail.trim();
    if (!email) {
      setBuyerEmailError('Please enter your email address.');
      errors.push('email');
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      setBuyerEmailError('Please enter a valid email address.');
      errors.push('email');
    }

    // If there are buyer field errors, return generic message
    // (specific errors shown inline)
    if (errors.length > 0) {
      return 'Please fill in all required fields.';
    }

    if (!agreeToTerms) return 'You must accept the Terms & Conditions to continue.';
    if (!incoming.firstName) return 'Recipient first name is missing.';
    if (!incoming.lastName) return 'Recipient last name is missing.';
    if (!incoming.email) return 'Recipient email is missing.';
    if (amount <= 0) return 'Gift card amount must be greater than 0.';
    const recipientEmail = isSelfDelivery
      ? email
      : (incoming.email ?? '').trim().toLowerCase();
    if (!recipientEmail) return 'Recipient email is missing.';
    return null;
  };

  const handlePay = async () => {
    if (submitting) return;

    const validationError = getValidationError();
    if (validationError) {
      // Buyer-field problems also render inline, but the terms-checkbox
      // and recipient-field errors have no inline slot — without
      // surfacing the reason here the "Pay Securely" button just does
      // nothing when one of those fails, which reads as a dead button.
      toast.error(validationError);
      return;
    }

    // When the buyer chose "send to me first", the recipient_email is
    // still required by the backend — fall back to the buyer's address
    // so the row is internally consistent.
    const recipientEmail = isSelfDelivery
      ? buyerEmail.trim().toLowerCase()
      : (incoming.email ?? '').trim().toLowerCase();

    // Persist the buyer info so a refresh on the Stripe-redirect path
    // can recover the customer's typed details if needed.
    patchGiftCardDraft({
      buyerFirstName: buyerFirst.trim(),
      buyerLastName: buyerLast.trim(),
      buyerEmail: buyerEmail.trim().toLowerCase(),
    });
    setSubmitting(true);
    try {
      const res = await purchaseGiftCard({
        amount,
        buyer_first_name: buyerFirst.trim(),
        buyer_last_name: buyerLast.trim(),
        buyer_email: buyerEmail.trim().toLowerCase(),
        recipient_first_name: (incoming.firstName ?? '').trim(),
        recipient_last_name: (incoming.lastName ?? '').trim(),
        recipient_email: recipientEmail,
        message: incoming.message?.trim() || null,
        occasion: incoming.occasion || null,
        delivery_method: isSelfDelivery ? 'SELF' : 'RECIPIENT',
        send_date: incoming.sendDate || null,
      });
      // Stripe Checkout is a hard navigation — handing off to a
      // separate domain. The success URL already encodes the session
      // id so /gift-card/sent can render the buyer's code without
      // any local state.
      if (!res.checkout_url) {
        throw new Error('Missing Stripe checkout URL in response');
      }
      toast.success('Payment successful! Redirecting to payment...');
      window.location.href = res.checkout_url;
      return;
    } catch (err) {
      const msg = giftCardApiErrorMessage(
        err,
        'Could not complete your purchase. Please try again.',
      );
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const currentStep = 3;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 py-8 md:py-12 pb-32 md:pb-12 w-full">
        <GiftCardStepper currentStep={currentStep} />

        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left column */}
          <div className="flex-1 max-w-[740px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] tracking-[-0.0857px] mb-2">
              Your Details
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px] mb-6">
              We need a few quick details to send your receipt.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <FloatingField
                  label="Your First Name *"
                  placeholder="e.g. Sarah"
                  value={buyerFirst}
                  onChange={(v) => {
                    setBuyerFirst(sanitizeName(v));
                    setBuyerFirstError(null);
                  }}
                  error={!!buyerFirstError}
                />
                {buyerFirstError && (
                  <p className="text-[#991b1b] text-[12px] mt-1 font-['Inter',sans-serif]">
                    {buyerFirstError}
                  </p>
                )}
              </div>
              <div>
                <FloatingField
                  label="Your Last Name *"
                  placeholder="e.g. Williams"
                  value={buyerLast}
                  onChange={(v) => {
                    setBuyerLast(sanitizeName(v));
                    setBuyerLastError(null);
                  }}
                  error={!!buyerLastError}
                />
                {buyerLastError && (
                  <p className="text-[#991b1b] text-[12px] mt-1 font-['Inter',sans-serif]">
                    {buyerLastError}
                  </p>
                )}
              </div>
            </div>
            <div className="mb-2">
              <FloatingField
                label="Your Email Address *"
                placeholder="abc@gmail.com"
                type="email"
                value={buyerEmail}
                onChange={(v) => {
                  setBuyerEmail(v);
                  setBuyerEmailError(null);
                }}
                error={!!buyerEmailError}
              />
              {buyerEmailError && (
                <p className="text-[#991b1b] text-[12px] mt-1 font-['Inter',sans-serif]">
                  {buyerEmailError}
                </p>
              )}
            </div>
            <p className="font-['Inter',sans-serif] text-[#3a3335] text-[12px] mb-10">
              We'll send your  order confirmation here
            </p>

            <div className="h-px bg-[#ede8e9] w-full mb-10" />

            {/* Order Summary */}
            <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-[36px] tracking-[-0.0857px] mb-2">
              Order Summary
            </h2>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px] mb-5">
              Please check everything is correct before you checkout
            </p>

            <div className="bg-white border border-[#ede8e9] rounded-[10px] p-6">
              <SummaryRow label="Gift Card Value" valueClassName="text-[#8e2a2f]">
                £{amount.toFixed(2)}
              </SummaryRow>
              <SummaryRow label="For">{recipientName}</SummaryRow>
              <SummaryRow label="Send to">
                {isSelfDelivery
                  ? buyerEmail || 'Me'
                  : incoming.email || '-'}
              </SummaryRow>
              <SummaryRow label="Delivery Method">
                {isSelfDelivery ? 'To me first' : 'Directly to recipient'}
              </SummaryRow>
              <SummaryRow label="Send Date" last>
                {formatDate(incoming.sendDate)}
              </SummaryRow>
              <div className="flex items-center justify-between mt-5">
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px]">
                  Total To Pay
                </span>
                <span className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] leading-[27px]">
                  £{amount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-8 mb-5 max-w-[475px] mx-auto">
              <div className="flex items-start gap-3 mb-4">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreeToTerms}
                  onChange={(e) => {
                    setAgreeToTerms(e.target.checked);
                  }}
                  className="mt-1 w-4 h-4 cursor-pointer accent-[#8e2a2f]"
                />
                <label htmlFor="agree-terms" className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[12px] tracking-[-0.1px] cursor-pointer flex-1">
                  I agree to Cherry Picked{' '}
                  <button
                    type="button"
                    onClick={() => setTermsModalOpen(true)}
                    className="text-[#8e2a2f] hover:underline font-bold"
                  >
                    Term's &amp; Conditions
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    onClick={() => setPrivacyModalOpen(true)}
                    className="text-[#8e2a2f] hover:underline font-bold"
                  >
                    Privacy Policy
                  </button>
                  . Gift cards are non refundable.
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="hidden md:flex items-center gap-3.5 justify-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-[54px] w-[110px] rounded-[10px] bg-[#3a33350a] hover:bg-[#3a333514] flex items-center justify-center gap-1.5 cursor-pointer transition-colors"
                disabled={submitting}
              >
                <span className="material-icons text-[18px] text-[#908e8e]">arrow_back</span>
                <span className="font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#908e8e] tracking-[-0.1px]">
                  Back
                </span>
              </button>
              <PrimaryButton
                onClick={handlePay}
                className="w-full max-w-[420px] h-[54px]"
                disabled={submitting}
              >
                {submitting ? 'Processing…' : 'Pay Securely'}
              </PrimaryButton>
            </div>

            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-[#eee] md:hidden z-50">
              <PrimaryButton
                onClick={handlePay}
                className="w-full h-[54px]"
                disabled={submitting}
              >
                {submitting ? 'Processing…' : 'Pay Securely'}
              </PrimaryButton>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-full lg:w-[361px] shrink-0">
            <YourGiftCardPreview
              amount={amount}
              recipientName={recipientName !== '-' ? recipientName : undefined}
            />

            <div className="mt-5 space-y-3">
              <div className="flex items-start gap-2.5">
                <span className="material-icons text-[#8e2a2f] text-[20px] leading-[21px]">
                  redeem
                </span>
                <p className="font-['DM_Sans',sans-serif] font-medium text-[#3a3335] text-[14px] leading-[21px] tracking-[-0.1px]">
                  Redeemable against any Cherry Picked service
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {termsModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-[#8e2a2f] font-bold text-[20px]">Terms &amp; Conditions</h2>
              <button
                onClick={() => setTermsModalOpen(false)}
                className="text-[#908e8e] hover:text-[#3a3335]"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="p-6 text-[14px] text-[#3a3335] space-y-4">
              <p>By using Cherry Picked's gift card service, you agree to these terms and conditions.</p>
              <h3 className="font-bold mt-4">1. Gift Card Use</h3>
              <p>Gift cards can be redeemed for any Cherry Picked service. They are non-transferable and cannot be exchanged for cash.</p>
              <h3 className="font-bold mt-4">2. Expiration</h3>
              <p>Gift cards do not expire and can be used at any time.</p>
              <h3 className="font-bold mt-4">3. Non-Refundable</h3>
              <p>Gift cards are non-refundable. If you have questions, please contact our support team.</p>
              <h3 className="font-bold mt-4">4. Terms Acceptance</h3>
              <p>By purchasing a gift card, you confirm that you have read and agreed to these terms and conditions.</p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setTermsModalOpen(false)}
                className="px-6 py-2 rounded-lg bg-[#f4f4f4] text-[#3a3335] font-bold hover:bg-[#eee]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {privacyModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-[#8e2a2f] font-bold text-[20px]">Privacy Policy</h2>
              <button
                onClick={() => setPrivacyModalOpen(false)}
                className="text-[#908e8e] hover:text-[#3a3335]"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="p-6 text-[14px] text-[#3a3335] space-y-4">
              <p>Cherry Picked is committed to protecting your privacy and ensuring you have a positive experience on our platform.</p>
              <h3 className="font-bold mt-4">1. Information We Collect</h3>
              <p>We collect information necessary to process your gift card purchase, including your name, email address, and payment information.</p>
              <h3 className="font-bold mt-4">2. How We Use Your Information</h3>
              <p>Your information is used to process orders, send confirmations, and improve our services. We do not share your information with third parties without your consent.</p>
              <h3 className="font-bold mt-4">3. Data Protection</h3>
              <p>We use industry-standard security measures to protect your personal information.</p>
              <h3 className="font-bold mt-4">4. Contact Us</h3>
              <p>If you have questions about our privacy practices, please contact our support team.</p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => setPrivacyModalOpen(false)}
                className="px-6 py-2 rounded-lg bg-[#f4f4f4] text-[#3a3335] font-bold hover:bg-[#eee]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  children: React.ReactNode;
  last?: boolean;
  valueClassName?: string;
}

function SummaryRow({ label, children, last, valueClassName = '' }: SummaryRowProps) {
  return (
    <>
      <div className="flex items-center justify-between py-3 font-['Inter',sans-serif] text-[16px]">
        <span className="text-[#908e8e] leading-none">{label}</span>
        <span className={`text-[#3a3335] text-right leading-[24px] ${valueClassName}`}>
          {children}
        </span>
      </div>
      {!last && <div className="h-px bg-[#ede8e9] w-full" />}
    </>
  );
}

interface FloatingFieldProps {
  label: string;
  placeholder?: string;
  value: string;
  type?: string;
  onChange: (v: string) => void;
  error?: boolean;
}

function FloatingField({ label, placeholder, value, type = 'text', onChange, error = false }: FloatingFieldProps) {
  return (
    <div className="relative">
      <label className="absolute left-[20px] top-[10px] font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] pointer-events-none">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-[64px] pt-[26px] pb-[10px] px-[20px] bg-white border rounded-[8px] outline-none font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] placeholder:text-[#908e8e] placeholder:font-bold tracking-[-0.1px] transition-colors ${
          error
            ? 'border-[#991b1b] focus:border-[#991b1b]'
            : 'border-[#eee] focus:border-[#8e2a2f]'
        }`}
      />
    </div>
  );
}
